import React from "react";
import { useTheme } from "styled-components";
import { useAtlassianLocale } from "@Shared/Hooks/useAtlassianLocale";
import { buildDateAndSplit } from "../../../dateFormatting";
import {
  Wrapper,
  CardContainer,
  ListContainer,
  IconEmojiContainer,
  Date,
  DateIconContainer,
  TitleSubtitle,
  Title,
  Subtitle,
  TextArrowContainer,
  Text,
  CardHeader,
} from "./styles";
import Arrow from "../Arrow";
import Icon, { getIconType } from "../../../../../components/ui/icon/Icon";
import { LAYOUT_STRUCTURE_OPTIONS } from "../../../../../optics/components/drawers/settings/features/announcements/defaults";
import EditableLine from "../../inline-edit/EditableLine";
import EditableText from "../../inline-edit/EditableText";
import { handleLinkClick } from "../../../../../helpers/handle-link-click";

const List = ({ data, config, isEditable, isTemplate = false }) => {
  const {
    title,
    text,
    linkText,
    builtDate,
    color,
    textColor,
    emojiEnabled,
    emoji,
    icon,
    hasLink,
  } = data;
  const { borderRadius, layoutStructure } = config;

  const iconType = getIconType(icon);

  const theme = useTheme();

  return (
    <Wrapper
      isList
      isTemplate={isTemplate}
      borderRadius={borderRadius}
      hasLink={hasLink}
      onClick={() => {
        if (hasLink) {
          handleLinkClick({
            url: data?.url || {},
            newTab: data?.newTab === "true",
          });
        }
      }}
    >
      <ListContainer>
        <DateIconContainer
          isList
          style={{
            paddingBottom: layoutStructure.includes(
              LAYOUT_STRUCTURE_OPTIONS.ICON
            )
              ? "0px"
              : "14px",
          }}
        >
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.ICON) && (
            <IconEmojiContainer isList clr={color[theme.name]}>
              {emojiEnabled ? (
                emoji.native
              ) : (
                <>
                  {iconType === "app" ? (
                    <img src={icon} alt="img" height="35px" />
                  ) : (
                    <Icon icon={icon} size="35px" />
                  )}
                </>
              )}
            </IconEmojiContainer>
          )}
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) &&
            !!Object.keys(builtDate).length && (
              <Date
                isList
                bgClr={color[theme.name]}
                textClr={textColor[theme.name]}
                style={{
                  position: layoutStructure.includes(
                    LAYOUT_STRUCTURE_OPTIONS.ICON
                  )
                    ? "relative"
                    : "unset",
                  top: "-24px",
                  right: "-30px",
                }}
              >
                <span>{builtDate?.month?.toUpperCase()}</span>
                <span className="date-day">{builtDate?.day}</span>
              </Date>
            )}
        </DateIconContainer>
        <TitleSubtitle isList>
          <Title isList>
            <EditableText
              announcementId={data.id}
              isEditable={isEditable}
              value={title}
              propKey="title"
              disableNewLine
            />
          </Title>
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.TEXT) && (
            <Subtitle>
              <EditableText
                announcementId={data.id}
                isEditable={isEditable}
                value={text}
                propKey="text"
              />
            </Subtitle>
          )}
        </TitleSubtitle>

        <TextArrowContainer>
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.LINK) && (
            <Text>
              <EditableLine
                isEditable={isEditable}
                value={linkText}
                propKey="linkText"
              />
            </Text>
          )}
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.ARROW) && (
            <Arrow size="18" />
          )}
        </TextArrowContainer>
      </ListContainer>
    </Wrapper>
  );
};

const Card = ({ data, config, isEditable, isTemplate }) => {
  const {
    title,
    text,
    linkText,
    builtDate,
    color,
    textColor,
    emojiEnabled,
    emoji,
    icon,
    hasLink,
  } = data;
  const { borderRadius, layoutStructure } = config;

  const iconType = getIconType(icon);

  const theme = useTheme();

  return (
    <Wrapper
      isTemplate={isTemplate}
      borderRadius={borderRadius}
      hasLink={hasLink}
      onClick={() => {
        if (hasLink) {
          handleLinkClick({
            url: data?.url || {},
            newTab: data?.newTab === "true",
          });
        }
      }}
    >
      <CardContainer>
        <CardHeader>
          <DateIconContainer>
            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) &&
              !!Object.keys(builtDate).length && (
                <Date bgClr={color[theme.name]} textClr={textColor[theme.name]}>
                  <span>{builtDate.month.toUpperCase()}</span>
                  <span className="date-day">{builtDate.day}</span>
                </Date>
              )}
            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.ICON) && (
              <IconEmojiContainer
                clr={color[theme.name]}
                style={{
                  top: "28px",
                  position:
                    layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.DATE) &&
                    !!Object.keys(builtDate).length
                      ? "relative"
                      : "unset",
                  left: "-22px",
                }}
              >
                {emojiEnabled ? (
                  emoji.native
                ) : (
                  <>
                    {iconType === "app" ? (
                      <img src={icon} alt="img" height="25px" />
                    ) : (
                      <Icon icon={icon} size="25px" />
                    )}
                  </>
                )}
              </IconEmojiContainer>
            )}
          </DateIconContainer>

          <TextArrowContainer>
            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.LINK) && (
              <Text>
                <EditableLine
                  isEditable={isEditable}
                  value={linkText}
                  propKey="linkText"
                />
              </Text>
            )}
            {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.ARROW) && (
              <Arrow size="18" />
            )}
          </TextArrowContainer>
        </CardHeader>
        <TitleSubtitle>
          <Title>
            <EditableText
              announcementId={data.id}
              isEditable={isEditable}
              value={title}
              propKey="title"
              disableNewLine
            />
          </Title>
          {layoutStructure.includes(LAYOUT_STRUCTURE_OPTIONS.TEXT) && (
            <Subtitle>
              <EditableText
                announcementId={data.id}
                isEditable={isEditable}
                value={text}
                propKey="text"
              />
            </Subtitle>
          )}
        </TitleSubtitle>
      </CardContainer>
    </Wrapper>
  );
};

function Notification({ data, config, isEditable, viewMode, isTemplate }) {
  const [locale] = useAtlassianLocale();
  const { date } = data;

  const isList = config.view === "list";

  const builtDate = buildDateAndSplit(date, false, locale, config.designStyle);
  const hasLink = viewMode === "view" && !!data?.url?.link;
  const props = {
    data: { ...data, hasLink, builtDate },
    config,
    isEditable,
    isTemplate,
  };

  return isList ? <List {...props} /> : <Card {...props} />;
}

export default Notification;
